import Modal from "components/modal/Modal";
import { TempoChat, Chat, I18nProvider } from "@acoladgroup/tempo-messaging-library";
import { useUser } from "query/user.query";
import {
  useChatMessagePageQuery,
  useSubmitMessageMutation,
  useUpdateLastReadMessageMutation,
} from "query/tempo-chat.query";
import { useEffect, useState, useCallback } from "react";
import { useHubConnection } from "store/useHubConnection";
import { useTranslation } from "react-i18next";
import TempoChatHeader from "./TempoChatHeader";
import { getParticipants } from "api/tempo-chat.api";

interface TempoChatModalProps {
  show: boolean;
  tempoChatData: Chat;
  onClose: () => void;
}

const TempoChatModal = ({ show, tempoChatData, onClose }: TempoChatModalProps) => {
  const { i18n } = useTranslation();
  const { data: user } = useUser();
  const [currentParticipantId, setCurrentParticipantId] = useState<number | undefined>();
  const getChatMessagePage = useChatMessagePageQuery();
  const submitMessageMutation = useSubmitMessageMutation();
  const updateLastReadMessageMutation = useUpdateLastReadMessageMutation();

  const hub = useHubConnection((state) => state.connection);

  useEffect(() => {
    if (!user) return;
    const possibleUserIds = user.similarContactIds.concat([user.id]);
    const found = tempoChatData.participants.find((p) => p.contactId && possibleUserIds.includes(p.contactId));
    setCurrentParticipantId(found ? found.id : undefined);
  }, [user, tempoChatData]);

  const handleGetChatPage = useCallback(
    (pageNumber: number, messageToStartFrom?: number) =>
      getChatMessagePage(tempoChatData.id, pageNumber, messageToStartFrom),
    [tempoChatData.id, getChatMessagePage]
  );

  const handleSubmitMessage = useCallback(
    (message: string) => submitMessageMutation.mutateAsync({ chatId: tempoChatData.id, text: message }),
    [tempoChatData.id, submitMessageMutation]
  );

  const handleUpdateLastReadMessage = useCallback(
    (lastReadMessageId: number) =>
      updateLastReadMessageMutation.mutateAsync({ chatId: tempoChatData.id, lastReadMessageId }),
    [tempoChatData.id, updateLastReadMessageMutation]
  );

  const handleGetParticipants = useCallback(() => getParticipants(tempoChatData.id), [tempoChatData.id]);

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Modal isShown={show} onClose={onClose} maxWidthClassName={"sm:max-w-3xl"} showCloseButton>
      <div className="h-[600px]" onClick={handleModalClick}>
        {tempoChatData && (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          <I18nProvider i18nInstance={i18n as any}>
            <TempoChat
              currentParticipantId={currentParticipantId}
              authenticatedUserId={user!.id}
              initialChatData={tempoChatData}
              getChatPage={handleGetChatPage}
              submitMessage={handleSubmitMessage}
              updateLastReadMessage={handleUpdateLastReadMessage}
              getParticipants={handleGetParticipants}
              hubConnection={hub}
              debug={false}
              headerComponent={() => TempoChatHeader({ chatData: tempoChatData })}
            />
          </I18nProvider>
        )}
      </div>
    </Modal>
  );
};

export default TempoChatModal;
